import React, { useState, useEffect, useRef } from "react";
import CountUp from "react-countup";

interface BusinessImpactProps {
  metrics?: {
    automatedTasks: number;
    timeOptimized: number;
    costsReduced: number;
  };
  onSectionVisible?: (section: string) => void;
}

const BusinessImpact: React.FC<BusinessImpactProps> = ({
  metrics = { automatedTasks: 0, timeOptimized: 0, costsReduced: 0 },
  onSectionVisible,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (onSectionVisible) onSectionVisible("impact");
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, [onSectionVisible]);

  return (
    <section
      ref={sectionRef}
      id="business-impact"
      className="py-16 bg-[#f8fafc]"
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-10">
          <h2 className="text-4xl font-bold text-gray-800 mb-4">
            Real Business Impact
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Our AI solutions transform how businesses operate. See the
            measurable results our clients achieve with our technology.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="mb-4 mx-auto w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-blue-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5 4a3 3 0 00-3 3v6a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H5zm0 2a1 1 0 00-1 1v6a1 1 0 001 1h10a1 1 0 001-1V7a1 1 0 00-1-1H5z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h3 className="text-4xl font-bold text-gray-800 mb-2">
              {isVisible ? (
                <CountUp
                  start={0}
                  end={metrics.automatedTasks}
                  duration={2}
                  separator=","
                  suffix="+"
                />
              ) : (
                "0+"
              )}
            </h3>
            <h4 className="text-xl font-semibold text-purple-600 mb-2">
              Automated Tasks
            </h4>
            <p className="text-gray-500">Daily manual processes eliminated</p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="mb-4 mx-auto w-16 h-16 flex items-center justify-center bg-purple-100 rounded-full">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-purple-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h3 className="text-4xl font-bold text-gray-800 mb-2">
              {isVisible ? (
                <CountUp
                  start={0}
                  end={metrics.timeOptimized}
                  duration={2}
                  suffix="%"
                />
              ) : (
                "0%"
              )}
            </h3>
            <h4 className="text-xl font-semibold text-purple-600 mb-2">
              Time Savings
            </h4>
            <p className="text-gray-500">Average reduction in process time</p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="mb-4 mx-auto w-16 h-16 flex items-center justify-center bg-green-100 rounded-full">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-green-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h3 className="text-4xl font-bold text-gray-800 mb-2">
              {isVisible ? (
                <CountUp
                  start={0}
                  end={metrics.costsReduced}
                  duration={2}
                  suffix="%"
                />
              ) : (
                "0%"
              )}
            </h3>
            <h4 className="text-xl font-semibold text-purple-600 mb-2">
              Cost Reduction
            </h4>
            <p className="text-gray-500">Average operational cost savings</p>
          </div>
        </div>

        <div className="text-center mt-10">
          <a
            href="#contact"
            className="inline-block bg-purple-600 hover:bg-purple-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            Get Started
          </a>
        </div>
      </div>
    </section>
  );
};

export default BusinessImpact;
